import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  GbgrundstueckService,
  GebaeudeEingangAdresseListEntry,
  GebaeudeListEntry,
  GrundstueckOneFieldSearchListEntry,
  GrundstueckOneFieldSearchRequest,
  GrundstueckOneFieldSearchResponse
} from 'app/core/gb-api';
import { LinkComponent, NavTarget } from 'app/shared/components/link/link.component';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { CapExceptionHandler } from '../../../core/extension/exceptions/cap-exception-handler';
import { NgbTooltip, NgbTypeahead, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { AbstractTypeaheadSearch } from '../../../shared/components/typeahead-search/abstract-typeahead-search';
import { GrundstuecknummerPipe } from '../../../shared/pipes/simple/grundstuecknummer.pipe';
import { KompaktGebaeudeeingangsadressePipe } from '../../../shared/kompaktdarstellung/gebaeudeeingangsadresse/kompakt-gebaeudeeingangsadresse-pipe';
import { FormsModule } from '@angular/forms';
import { MetaDisablerDirective } from '../../../shared/directives/meta-disabler.directive';
import { MetaValidatorDirective } from '../../../shared/directives/meta-validator.directive';
import { TranslatePipe } from '@ngx-translate/core';

type AdressReducer = { element: GebaeudeListEntry; match: number };

@Component({
  selector: 'cap-search-bar',
  templateUrl: './searchbar.component.html',
  styleUrls: ['./searchbar.component.scss'],
  host: {
    class: 'form-control-component'
  },
  imports: [FormsModule, NgbTypeahead, MetaDisablerDirective, MetaValidatorDirective, TranslatePipe, NgbTooltip]
})
export class SearchbarComponent extends AbstractTypeaheadSearch<GrundstueckOneFieldSearchListEntry> implements OnInit {
  @Input()
  public disabled = false;

  GEBAEUDE_FIELD = 'gebaeude';
  GEBAEUDEEINGANGADRESSE_FIELD = 'gebaeudeeingangAdresse';
  FLURNAME_FIELD = 'flurname';

  minSearchLength = 2;
  showInputAfterSelect = false;
  debounceTime = 500;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private grundstueckService: GbgrundstueckService,
    private grundstuecknummerPipe: GrundstuecknummerPipe,
    private gebaeudeAdressePipe: KompaktGebaeudeeingangsadressePipe,
    private exceptionHandler: CapExceptionHandler
  ) {
    super();
  }

  ngOnInit() {
    keysToTranslate<string>(['searchbar.placeholderText', 'searchbar.searchingText', 'searchbar.noResults', 'searchbar.nichtRegistriert']);
  }

  search = (search$: Observable<string>) => {
    return search$.pipe(
      debounceTime(this.debounceTime),
      distinctUntilChanged(),
      switchMap((searchTerm) => {
        this.searchTerm = searchTerm.toLowerCase();
        if (searchTerm?.length >= this.minSearchLength) {
          return this.grundstueckService.searchGrundstueckOneField(this.createRequestObject(searchTerm)).pipe(
            catchError((err) => {
              this.exceptionHandler.handleError(err);
              return EMPTY;
            })
          );
        }
        return of([]);
      }),
      map((data: GrundstueckOneFieldSearchResponse) => {
        const results = data?.results ? data.results : [];
        this.results = results;
        return results;
      })
    );
  };

  public createRequestObject(searchQuery: string): GrundstueckOneFieldSearchRequest {
    return new (class implements GrundstueckOneFieldSearchRequest {
      searchQuery = searchQuery;
    })();
  }

  onSelected($event: NgbTypeaheadSelectItemEvent<GrundstueckOneFieldSearchListEntry>): void {
    const selectedItem: GrundstueckOneFieldSearchListEntry = $event.item;
    if (selectedItem) {
      const path = LinkComponent.getPath(NavTarget.NAV_GRUNDSTUECK_ES);
      this.router.navigate([path], {
        relativeTo: this.route,
        queryParams: { id: selectedItem.grundstueckId.id }
      });
    }
    if (!this.showInputAfterSelect) {
      this.value = '';
    }
  }

  formatEntry(entry: GrundstueckOneFieldSearchListEntry): string {
    let grundstueckNummer = this.grundstuecknummerPipe.transform(entry.grundstueckNummer);
    if (entry.grundstueckNummer.egrid) {
      grundstueckNummer += '; ' + entry.grundstueckNummer.egrid.value;
    }
    const displayAdressOrFlurname = this.getHighestScoreNestedElement(entry) || this.getAddress(entry) || this.getFirstFlurname(entry);
    return [grundstueckNummer, displayAdressOrFlurname].filter(Boolean).join('; ');
  }

  private getHighestScoreNestedElement(entry: GrundstueckOneFieldSearchListEntry): string {
    const gebaeude = entry.match.nestedMatchFieldOffset.find((a) => a.nestedFieldName === this.GEBAEUDE_FIELD);
    const gebaeudeeingangAdresse = entry.match.nestedMatchFieldOffset.find((a) => a.nestedFieldName === this.GEBAEUDEEINGANGADRESSE_FIELD);
    const flurname = entry.match.nestedMatchFieldOffset.find((a) => a.nestedFieldName === this.FLURNAME_FIELD);

    const gebaeudeeingangAdresseOffset = gebaeudeeingangAdresse ? gebaeudeeingangAdresse.nestedFieldMatchOffset : 0;

    if (flurname && gebaeude) {
      if (flurname.score > gebaeude.score) {
        return this.getNestedFlurname(entry, flurname.nestedFieldMatchOffset);
      }
      return this.gebaeudeAdressePipe.transform(this.getNestedAdress(entry, gebaeude.nestedFieldMatchOffset, gebaeudeeingangAdresseOffset));
    }

    if (gebaeude) {
      return this.gebaeudeAdressePipe.transform(this.getNestedAdress(entry, gebaeude.nestedFieldMatchOffset, gebaeudeeingangAdresseOffset));
    }

    if (flurname) {
      return this.getNestedFlurname(entry, flurname.nestedFieldMatchOffset);
    }
  }

  private getFirstFlurname(entry: GrundstueckOneFieldSearchListEntry): string {
    return this.getNestedFlurname(entry, 0);
  }

  private getAddress(entry: GrundstueckOneFieldSearchListEntry): string {
    const index: number = this.getBestMatchingAdress(entry);
    const address: GebaeudeEingangAdresseListEntry = this.getNestedAdress(entry, index, 0);
    if (address) {
      return this.gebaeudeAdressePipe.transform(address);
    }
    return undefined;
  }

  private getBestMatchingAdress = (entry: GrundstueckOneFieldSearchListEntry): number => {
    const reduce: AdressReducer = entry.gebauede.reduce(
      (best: AdressReducer, g): AdressReducer => {
        const a: GebaeudeEingangAdresseListEntry = g.eingangAdresse[0];
        const match: number =
          this.match(a?.hausnummer !== null ? a?.strasse.toLowerCase() : '') +
          this.match(a?.hausnummer !== null ? a?.hausnummer.toLowerCase() : '') +
          this.match(a?.plz !== null ? a?.plz.toLowerCase() : '') +
          this.match(a?.plz !== null ? a?.ortschaft.toLowerCase() : '');
        if (match > best.match) {
          return { element: g, match: match };
        }
        return best;
      },
      { element: undefined, match: 0 }
    );
    return entry.gebauede.indexOf(reduce.element);
  };

  private match(s: string): number {
    return this.searchTerm.includes(s) ? 1 : 0;
  }

  private getNestedFlurname(entry: GrundstueckOneFieldSearchListEntry, offset: number): string {
    if (entry.flurnamen && entry.flurnamen[offset]) {
      return entry.flurnamen[offset];
    }
    return undefined;
  }

  private getNestedAdress(
    entry: GrundstueckOneFieldSearchListEntry,
    offsetGebaeude: number,
    offsetEingangAdresse: number
  ): GebaeudeEingangAdresseListEntry {
    if (
      entry.gebauede[offsetGebaeude] &&
      entry.gebauede[offsetGebaeude].eingangAdresse &&
      entry.gebauede[offsetGebaeude].eingangAdresse[offsetEingangAdresse]
    ) {
      return entry.gebauede[offsetGebaeude].eingangAdresse[offsetEingangAdresse];
    }
    return undefined;
  }
}
